import { Provider as MobxProvider } from 'mobx-react';
import React, { useEffect } from 'react';
import { ApolloProvider as ApolloProviderBase } from 'react-apollo';
import SessionReplay, { LDRecord } from '@launchdarkly/session-replay';
import { asyncWithLDProvider, useLDClient } from 'launchdarkly-react-client-sdk';
import { ThemeProvider as StyledComponentsProvider } from 'styled-components';
import { ThemeProvider as MuiProvider, StylesProvider } from '@material-ui/core/styles';

import PublicEnv from 'shared/utils/public-env';
import { setLDClient } from 'shared/observability/ld-client-ref';
import { registerSessionReplayUrlProvider } from 'shared/observability/ld-observability';
import { LD_APPLICATION_CONTEXT } from 'src/constants/launch-darkly';
import { useLinkDatadogRumToLd } from 'src/hooks/use-link-datadog-rum-to-ld';
import { appState } from 'src/state/app';
import DutchieRouter from 'src/routes';
import Ernie from 'shared/components/ernie';
import { styledComponentsTheme } from 'shared/themes';
import { PendoAnalytics } from 'src/utils/pendo';
import { GlobalStyles, adminMuiTheme } from './styles';

const ApolloProvider = ApolloProviderBase as any;

function LDClientBridge(): null {
  // Exposes the LD client to non-React modules (Apollo links, identify call sites).
  // No cleanup: the bridge lives at the root for the entire app lifetime, and the
  // LD client is a stable singleton, so an unmount-time clear would only create a
  // transient undefined window during React 18 strict-mode rerenders.
  const ldClient = useLDClient();
  useEffect(() => {
    setLDClient(ldClient ?? undefined);
  }, [ldClient]);
  return null;
}

function DatadogRumLdBridge(): null {
  useLinkDatadogRumToLd();
  return null;
}

function Root(): JSX.Element {
  return (
    <MobxProvider {...appState.stores()}>
      <ApolloProvider client={appState.apolloClient}>
        <StyledComponentsProvider theme={styledComponentsTheme}>
          <MuiProvider theme={adminMuiTheme}>
            <StylesProvider injectFirst>
              <>
                <LDClientBridge />
                <DatadogRumLdBridge />
                <GlobalStyles />
                <PendoAnalytics />
                <DutchieRouter />
                <Ernie />
              </>
            </StylesProvider>
          </MuiProvider>
        </StyledComponentsProvider>
      </ApolloProvider>
    </MobxProvider>
  );
}

/**
 * Initializes LaunchDarkly asynchronously and returns the LD Provider component.
 * This ensures feature flags are available before the app renders.
 */
export async function initializeLDProvider(): Promise<React.ComponentType<{ children: React.ReactNode }>> {
  const sessionReplayPlugins =
    PublicEnv.appEnv === 'production' || PublicEnv.appEnv === 'stage'
      ? [new SessionReplay({ privacySetting: 'default' })]
      : undefined;

  if (sessionReplayPlugins) {
    registerSessionReplayUrlProvider(() => LDRecord.getSession()?.urlWithTimestamp);
  }

  return asyncWithLDProvider({
    clientSideID: PublicEnv.launchDarklyClientId,
    timeout: 5,
    context: {
      kind: 'multi',
      application: LD_APPLICATION_CONTEXT,
    },
    options: {
      sendLDHeaders: false, // Disable custom headers to eliminate OPTIONS preflight for CORS
      bootstrap: 'localStorage',
      // Configure relay proxy endpoints (SDK v3.x uses baseUrl, streamUrl, eventsUrl)
      ...(PublicEnv.launchDarklyBaseUri && { baseUrl: PublicEnv.launchDarklyBaseUri }),
      ...(PublicEnv.launchDarklyStreamUri && { streamUrl: PublicEnv.launchDarklyStreamUri }),
      ...(PublicEnv.launchDarklyEventsUri && { eventsUrl: PublicEnv.launchDarklyEventsUri }),
      ...(sessionReplayPlugins && { plugins: sessionReplayPlugins }),
    },
    reactOptions: { useCamelCaseFlagKeys: false },
  });
}

export default Root;
